import { useState, useEffect, useCallback } from 'react';
import { useAsyncData, useAsyncMutation } from './useAsyncData';
import type {
  ConversationPractice,
  ConversationPracticeFilters,
  ConversationPracticeFormData,
  ConversationPracticeListItem,
  ContentItem,
} from '@/types/conversation.types';
import type { Activity } from '@/types/story.types';
import * as conversationsApi from '@/services/api/conversations.api';

/** Práctica de conversación publicada (GET /conversations). */
export function usePublishedConversationPractice(resourceId: string | undefined) {
  const { data, isLoading, error } = useAsyncData<ConversationPractice | null>({
    fetchFn: () =>
      resourceId
        ? conversationsApi.getPublishedConversationPracticeById(resourceId)
        : Promise.resolve(null),
    dependencies: [resourceId],
  });
  return { resource: data ?? null, isLoading, error };
}

export function usePublishedConversationPractices(
  filters?: Pick<ConversationPracticeFilters, 'level'>
) {
  const { data, isLoading, error, refetch } = useAsyncData<{
    resources: ConversationPracticeListItem[];
    total?: number;
  }>({
    fetchFn: () => conversationsApi.getPublishedConversationPractices(filters),
    dependencies: [filters?.level],
  });
  return { resources: data?.resources ?? [], total: data?.total ?? 0, isLoading, error, refetch };
}

export function useBackofficeConversationPractices(filters?: ConversationPracticeFilters) {
  const { data, isLoading, error } = useAsyncData<{
    resources: ConversationPracticeListItem[];
    total: number;
  }>({
    fetchFn: () =>
      conversationsApi.getBackofficeConversationPractices({
        searchQuery: filters?.searchQuery,
        level: filters?.level,
        status: filters?.status,
      }),
    dependencies: [filters],
  });
  return { resources: data?.resources ?? [], total: data?.total ?? 0, isLoading, error };
}

export function useSaveConversationPractice() {
  const { mutate, isLoading: isSaving, error } = useAsyncMutation<
    ConversationPracticeListItem,
    { resourceId: string | null; data: ConversationPracticeFormData }
  >({
    mutationFn: ({ resourceId, data }) =>
      conversationsApi.saveConversationPractice(resourceId, data),
  });
  return {
    saveConversationPractice: (resourceId: string | null, data: ConversationPracticeFormData) =>
      mutate({ resourceId, data }),
    isSaving,
    error,
  };
}

/** Carga una práctica de conversación para el backoffice. */
export function useBackofficeConversationPractice(resourceId: string | undefined) {
  const { data, isLoading, error, refetch } = useAsyncData<ConversationPractice | null>({
    fetchFn: () =>
      resourceId
        ? conversationsApi.getBackofficeConversationPracticeById(resourceId)
        : Promise.resolve(null),
    dependencies: [resourceId],
    enabled: !!resourceId,
  });
  return { resource: data ?? null, isLoading, error, refetchResource: refetch };
}

export function useConversationContent(resourceId?: string) {
  const [content, setContent] = useState<ContentItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const refetch = useCallback(async () => {
    if (!resourceId) {
      setContent([]);
      setIsLoading(false);
      return;
    }
    try {
      setIsLoading(true);
      const data = await conversationsApi.getConversationPracticeContent(resourceId);
      setContent(data.sort((a, b) => a.order - b.order));
      setError(null);
    } catch (err) {
      setError(err as Error);
    } finally {
      setIsLoading(false);
    }
  }, [resourceId]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  const saveContent = async (contentData: Partial<ContentItem> & { id?: string }) => {
    if (!resourceId) throw new Error('Resource ID is required');
    try {
      const result = await conversationsApi.saveConversationPracticeContent(
        resourceId,
        contentData
      );
      if (contentData.id) {
        setContent((prev) => prev.map((c) => (c.id === result.id ? result : c)));
      } else {
        setContent((prev) => [...prev, result].sort((a, b) => a.order - b.order));
      }
      return result;
    } catch (err) {
      setError(err as Error);
      throw err;
    }
  };

  const deleteContent = async (contentId: string) => {
    if (!resourceId) throw new Error('Resource ID is required');
    try {
      await conversationsApi.deleteConversationPracticeContent(resourceId, contentId);
      setContent((prev) => prev.filter((c) => c.id !== contentId));
    } catch (err) {
      setError(err as Error);
      throw err;
    }
  };

  return { content, isLoading, error, saveContent, deleteContent, refetch };
}

export function useConversationActivities(resourceId?: string) {
  const [activities, setActivities] = useState<Activity[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!resourceId) {
      setIsLoading(false);
      return;
    }

    const fetchActivities = async () => {
      try {
        setIsLoading(true);
        const data = await conversationsApi.getConversationPracticeActivities(resourceId);
        setActivities(data.sort((a, b) => a.order - b.order));
        setError(null);
      } catch (err) {
        setError(err as Error);
      } finally {
        setIsLoading(false);
      }
    };

    void fetchActivities();
  }, [resourceId]);

  const saveActivity = async (activityId: string | null, activityData: Partial<Activity>) => {
    if (!resourceId) throw new Error('Resource ID is required');
    try {
      const result = await conversationsApi.saveConversationPracticeActivity(
        resourceId,
        activityId,
        activityData
      );
      if (activityId) {
        setActivities((prev) => prev.map((a) => (a.id === result.id ? result : a)));
      } else {
        setActivities((prev) => [...prev, result].sort((a, b) => a.order - b.order));
      }
      return result;
    } catch (err) {
      setError(err as Error);
      throw err;
    }
  };

  const deleteActivity = async (activityId: string) => {
    if (!resourceId) throw new Error('Resource ID is required');
    try {
      await conversationsApi.deleteConversationPracticeActivity(resourceId, activityId);
      setActivities((prev) => prev.filter((a) => a.id !== activityId));
    } catch (err) {
      setError(err as Error);
      throw err;
    }
  };

  const reorderActivity = async (activityId: string, newOrder: number) => {
    if (!resourceId) throw new Error('Resource ID is required');
    try {
      await conversationsApi.reorderConversationPracticeActivity(
        resourceId,
        activityId,
        newOrder
      );
      setActivities((prev) => {
        const updated = prev.map((a) =>
          a.id === activityId ? { ...a, order: newOrder } : a
        );
        return updated.sort((a, b) => a.order - b.order);
      });
    } catch (err) {
      setError(err as Error);
      throw err;
    }
  };

  return { activities, isLoading, error, saveActivity, deleteActivity, reorderActivity };
}
